import React, { useEffect, useRef } from "react";
import {
  FaShieldAlt,
  FaLock,
  FaUserShield,
  FaDatabase,
  FaGavel,
  FaPhone,
  FaEnvelope,
  FaClock,
  FaCheckCircle,
  FaGlobe,
  FaInfoCircle,
} from "react-icons/fa";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import ScrollToTop from "../../components/ScrollToTop/ScrollToTop";
import { ROUTES } from "../../constants/routes";
import styles from "../Terms/Terms.module.css"; // Using same CSS as Terms

const PrivacyPolicyPage: React.FC = () => {
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    // Initialize scroll reveal animations
    const observerOptions = {
      threshold: 0.2,
      rootMargin: "0px 0px -20px 0px",
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("revealed");
        }
      });
    }, observerOptions);

    // Observe all scroll reveal elements
    const elementsToObserve = document.querySelectorAll(
      ".scroll-reveal, .scroll-reveal-left, .scroll-reveal-right, .scroll-reveal-scale"
    );

    elementsToObserve.forEach((el) => {
      if (observerRef.current) {
        observerRef.current.observe(el);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const privacyData = [
    {
      id: "information-collection",
      icon: <FaDatabase />,
      title: "Information We Collect",
      content: [
        "Personal Information: Name, email address, phone number, date of birth, and profile details when you create an account.",
        "Identity Verification: Government-issued ID and verification documents for security purposes.",
        "Payment Information: Virtual bank account details and fee cashback transaction history within our secure payment system.",
        "Medical Records: Consultation notes, prescriptions, lab results, and treatment history shared with authorized healthcare providers.",
        "Health Data: Symptoms, medical conditions, allergies, medications, and other health-related information you provide.",
        "Communication Records: Messages with healthcare providers and support staff through the mobile app.",
        "Device Information: Mobile device type, operating system, app version, and device identifiers.",
        "Usage Data: App interactions, feature usage, session duration, and performance metrics to improve our services.",
        "Location Data: General location information to connect you with nearby healthcare providers (with your consent).",
      ],
    },
    {
      id: "information-use",
      icon: <FaUserShield />,
      title: "How We Use Your Information",
      content: [
        "Facilitate doctor consultations, lab appointments, and pharmacy services through our mobile platform.",
        "Process prescriptions and coordinate care between healthcare providers in our network.",
        "Maintain your medical records and health history for continuity of care and treatment.",
        "Enable referrals to specialists and seamless information sharing between authorized providers.",
        "Process fee cashback rewards and manage virtual bank account transactions securely.",
        "Verify healthcare provider credentials and maintain quality standards across our platform.",
        "Provide customer support and troubleshoot technical issues through the mobile app.",
        "Send important updates about your account, services, and platform improvements.",
        "Analyze usage patterns to improve app functionality and user experience.",
        "Develop new features and services based on user needs and healthcare trends.",
        "Ensure platform security and prevent fraudulent activities or unauthorized access.",
        "Comply with legal and regulatory requirements for healthcare data protection.",
      ],
    },
    {
      id: "information-sharing",
      icon: <FaLock />,
      title: "Information Sharing & Disclosure",
      content: [
        "Healthcare Providers: We share your health information only with healthcare providers you choose to consult with through our mobile app.",
        "Authorized Network: This includes doctors, specialists, laboratories, and pharmacies within our network to ensure coordinated care.",
        "Service Providers: We may share limited information with trusted third-party service providers who help us operate our platform.",
        "Cloud Infrastructure: Secure cloud hosting and data storage providers with HIPAA-compliant systems.",
        "Payment Processing: Partners for fee cashback and withdrawal services with encrypted transaction data.",
        "Support Services: Customer support and communication tools that help us assist you better.",
        "Security Services: Fraud prevention and security monitoring services to protect your data.",
        "Legal Requirements: We may disclose your information when required by law, court order, or government regulation.",
        "Safety Protection: When we believe disclosure is necessary to protect your safety or the safety of others.",
        "Fraud Investigation: To investigate fraud or other illegal activities on our platform.",
        "Law Enforcement: To respond to legal processes or law enforcement requests as required.",
        "Terms Enforcement: To enforce our Terms of Service and platform policies.",
        "No Data Sales: We never sell, rent, or trade your personal or health information to third parties for marketing purposes.",
      ],
    },
    {
      id: "data-security",
      icon: <FaShieldAlt />,
      title: "Data Security & Protection",
      content: [
        "End-to-End Encryption: All health data is encrypted in transit and at rest using military-grade AES-256 encryption.",
        "Secure Infrastructure: Our platform is hosted on secure, HIPAA-compliant cloud infrastructure with multiple security layers.",
        "Multi-Factor Authentication: Additional security layers for healthcare provider access and administrative functions.",
        "Regular Security Audits: Continuous monitoring and testing of our security systems by independent experts.",
        "Role-Based Access: Controls ensuring only authorized personnel can access your data based on their role.",
        "Audit Trails: Comprehensive tracking of all access to your health information for security and compliance.",
        "Session Security: Automatic session timeouts and secure logout procedures to prevent unauthorized access.",
        "Staff Training: Regular employee training on data protection and privacy practices in healthcare.",
        "Secure Backups: Encrypted backups of your data to prevent loss while maintaining security.",
        "Disaster Recovery: Procedures to ensure service continuity and data protection during emergencies.",
        "Geographic Distribution: Data centers with redundancy to ensure availability and security.",
      ],
    },
    {
      id: "user-rights",
      icon: <FaGavel />,
      title: "Your Rights & Choices",
      content: [
        "View Your Data: Access all your personal and health information through the mobile app dashboard.",
        "Download Records: Export your medical records and consultation history in standard formats.",
        "Data Portability: Transfer your information to other healthcare providers when needed for continued care.",
        "Update Information: Modify your personal information and preferences anytime through the app.",
        "Delete Data: Request deletion of your account and associated data through app settings.",
        "Restrict Processing: Limit how we use your information in certain circumstances as permitted by law.",
        "Opt-Out Communications: Unsubscribe from marketing communications while maintaining essential service notifications.",
        "Withdraw Consent: Remove consent for optional data processing at any time through privacy settings.",
        "Manage Permissions: Control location sharing and notification preferences in the mobile app.",
        "Provider Access Control: Manage which healthcare providers can access your information.",
        "Privacy Support: Contact our privacy team for assistance with any data-related requests.",
        "Response Timeline: We respond to privacy requests within 30 days as required by applicable laws.",
      ],
    },
    {
      id: "data-retention",
      icon: <FaClock />,
      title: "Data Retention & Deletion",
      content: [
        "Medical Records: Retained for 7 years as required by healthcare regulations and professional standards.",
        "Account Information: Kept for the duration of your account plus 3 years for legal compliance purposes.",
        "Transaction Records: Stored for 5 years for financial and regulatory purposes including fee cashback history.",
        "Communication Logs: Maintained for 2 years for quality assurance and customer support purposes.",
        "Immediate Removal: Personal identifiers are immediately removed or anonymized upon account deletion.",
        "Secure Deletion: Health records are securely deleted according to healthcare regulations and industry standards.",
        "Backup Purging: All backups are purged within 90 days of the deletion request processing.",
        "Research Data: Some anonymized data may be retained for research and platform improvement purposes.",
        "Legal Holds: Data may be retained longer due to active legal proceedings or investigations.",
        "Regulatory Requirements: Retention may be extended for audit needs or regulatory compliance.",
        "Financial Obligations: Data related to outstanding fee cashback or financial matters may be retained longer.",
      ],
    },
  ];

  return (
    <>
      <Navbar />
      <ScrollToTop />

      <div className={styles.termsPage}>
        {/* Hero Section */}
        <section className={styles.hero}>
          <div className={styles.container}>
            <div className={styles.heroContent}>
              <div className={styles.heroBadge}>
                <FaShieldAlt className={styles.badgeIcon} />
                Privacy Policy
              </div>

              <h1 className={styles.heroTitle}>
                Privacy
                <span className={styles.heroAccent}> Policy</span>
              </h1>

              <p className={styles.heroSubtitle}>
                Your privacy and security are our highest priority. Learn how we
                protect, use, and manage your personal and health information on{" "}
                {ROUTES.COMPANY.NAME} with military-grade encryption and
                transparent practices.
              </p>

              <div className={styles.heroInfo}>
                <div className={styles.infoItem}>
                  <FaClock className={styles.infoIcon} />
                  <div>
                    <span className={styles.infoLabel}>Last Updated</span>
                    <span className={styles.infoValue}>January 2025</span>
                  </div>
                </div>
                <div className={styles.infoItem}>
                  <FaGlobe className={styles.infoIcon} />
                  <div>
                    <span className={styles.infoLabel}>Compliance</span>
                    <span className={styles.infoValue}>HIPAA & GDPR</span>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.heroVisual}>
              <div
                className={`${styles.heroImageContainer} ${styles.floatGentle}`}
              >
                <div className={`${styles.heroIcon} ${styles.heartbeatFlow}`}>
                  <FaShieldAlt />
                </div>
                <div className={styles.heroRipples}>
                  <div className={styles.ripple}></div>
                  <div className={styles.ripple}></div>
                  <div className={styles.ripple}></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Navigation */}
        <section className={styles.quickNav}>
          <div className={styles.container}>
            <h3 className={styles.quickNavTitle}>Quick Navigation</h3>
            <div className={styles.quickNavGrid}>
              {privacyData.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className={styles.quickNavItem}
                >
                  {section.icon}
                  <span>{section.title}</span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Privacy Policy Content */}
        <section className={styles.termsContent}>
          <div className={styles.container}>
            {privacyData.map((section, index) => (
              <div
                key={section.id}
                id={section.id}
                className={`${styles.termsSection} scroll-reveal`}
              >
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionIcon}>{section.icon}</div>
                  <h2 className={styles.sectionTitle}>
                    {index + 1}. {section.title}
                  </h2>
                </div>
                <div className={styles.sectionContent}>
                  {section.content.map((paragraph, idx) => (
                    <p key={idx} className={styles.paragraph}>
                      <FaCheckCircle className={styles.bulletIcon} />
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Contact Section */}
        <section className={styles.contactSection}>
          <div className={styles.container}>
            <div className={`${styles.contactContent} scroll-reveal`}>
              <div className={styles.contactIcon}>
                <FaInfoCircle />
              </div>
              <h2 className={styles.contactTitle}>
                Questions About This Policy?
              </h2>
              <p className={styles.contactSubtitle}>
                If you have any questions about this Privacy Policy, how we
                handle your data, or want to exercise your privacy rights,
                please contact our support team. We're here to help and respond
                within 24 hours.
              </p>
              <div className={styles.contactActions}>
                <a href={ROUTES.EXTERNAL.EMAIL} className={styles.contactBtn}>
                  <FaEnvelope />
                  <span>Email Support</span>
                </a>
                <a
                  href={ROUTES.EXTERNAL.EMERGENCY_HOTLINE}
                  className={styles.contactBtn}
                >
                  <FaPhone />
                  <span>{ROUTES.EXTERNAL.EMERGENCY_DISPLAY}</span>
                </a>
              </div>
              <div className={styles.contactNote}>
                <FaCheckCircle />
                <small>
                  Available 24/7 • Response within 24 hours • Nigeria-based
                  support
                </small>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
};

export default PrivacyPolicyPage;
